import * as React from 'react';
import { TriangleAlert } from 'lucide-react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ErrorBannerProps {
  /** Optional CSS class(es) merged onto the outer wrapper via cn() */
  className?: string;
  /** Error message content — plain text or a React node. No i18n in the lib; text arrives via props. */
  message: React.ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Compact destructive alert banner for inline error display.
 *
 * - No i18n — all text arrives via the `message` prop.
 * - Styled with `border-destructive/30 bg-destructive/10` for a subtle alert look.
 * - Leading `TriangleAlert` icon sized `w-3.5 h-3.5` with `shrink-0 mt-0.5` for alignment.
 * - Message rendered as `text-sm text-destructive` block (supports React.ReactNode).
 * - `role="alert"` on the root div for accessibility.
 */
export function ErrorBanner({ className, message }: ErrorBannerProps): React.JSX.Element {
  return (
    <div
      className={cn('flex items-start gap-2 p-2 rounded-md border border-destructive/30 bg-destructive/10', className)}
      role="alert"
    >
      <TriangleAlert className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />
      <div className="text-sm text-destructive">{message}</div>
    </div>
  );
}
